import { lazy, Suspense } from "react";
import {
  BrowserRouter as Router,
  Routes,
  Route,
  Navigate,
} from "react-router-dom";
import "./index.css";
import axios from "axios";
import { userAtom } from "./recoil/userAtoms";
import { useRecoilValue } from "recoil";
import { GoogleOAuthProvider } from "@react-oauth/google";

const SignUp = lazy(() => import("./pages/SignUp"));
const AcceptInvite = lazy(() => import("./pages/AcceptInvite"));
const InviteHandler = lazy(() => import("./pages/InviteHandler"));
const Onboard = lazy(() => import("./pages/Onboard"));
const ForgotPassword = lazy(() => import("./pages/ForgotPassword"));
const Workspaces = lazy(() => import("./pages/Workspaces"));
const ReviewApprovalRequest = lazy(() => import("./pages/ReviewApprovalRequest"));
const Notifications = lazy(() => import("./pages/Notifications"));
const Settings = lazy(() => import("./pages/Settings"));
const CreateApprovalRequestEmail = lazy(() => import("./pages/CreateApprovalRequestEmail"));
const EditApprovalRequestEmail = lazy(() => import("./pages/EditApprovalRequestEmail"));
const PlanSelect = lazy(() => import("./pages/PlanSelect"));
const Privacy = lazy(() => import("./pages/Privacy"));
const EarlyAccess = lazy(() => import("./pages/EarlyAccess"));
const PNF = lazy(() => import("./pages/404"));
const VerifyEmail = lazy(() => import("./pages/VerifyEmail"));
const Login = lazy(() => import("./pages/Login"));
const Verify2FaAndLogin = lazy(() => import("./pages/Verify2FaAndLogin"));
const Approvals = lazy(() => import("./pages/ApprovalR"));
const NewSettings = lazy(() => import("./pages/NewSettings"));
const CreateApprovalR = lazy(() => import("./pages/CreateApprovalR"));
const ApprovalRequestTimeL = lazy(() => import("./pages/ApprovalRequestTimeL"));
const EditApprovalR = lazy(() => import("./pages/EditApprovalR"));
const ReviewApprovalR = lazy(() => import("./pages/ReviewApprovalR"));
const NotificationList = lazy(() => import("./pages/Notificate"));
const Reset2FA = lazy(() => import("./pages/Reset2FA"));
const ExcelViewer = lazy(() => import("./components/ExcelViewer"));
const OauthCallback = lazy(() => import("./pages/OauthCallback"));
const SignaturePad = lazy(() => import("./pages/Test"));
const Requests = lazy(() => import("./pages/Requests"));
const RequestsR = lazy(() => import("./pages/RequestsR"));
const CreateApprovalDrop = lazy(() => import("./pages/CreateApprovalDrop"));
const CompleteApprovalDrop = lazy(() => import("./pages/CompleteApprovalDrop"));
const MyDrops = lazy(() => import("./pages/MyDrops"));
const SwiftCheck = lazy(() => import("./pages/SwiftCheck"));
const Profile = lazy(() => import("./pages/Profile"));
const AdminBackofficeAiPrompt = lazy(() => import("./pages/Admin/backoffice/AiPrompt"));
const AdminBackofficeLiveMonitor = lazy(() => import("./pages/Admin/backoffice/LiveMonitor"));
const AdminBackofficePromptTemplates = lazy(() => import("./pages/Admin/backoffice/PromptTemplates"));
const AdminInstantMonitor = lazy(() => import("./pages/Admin/backoffice/InstantMonitor"));
const AdminBackofficeFeatureFlags = lazy(() => import("./pages/Admin/backoffice/FeatureFlags"));
const RequireBackofficeAdmin = lazy(() => import("./pages/Admin/backoffice/RequireBackofficeAdmin"));
const SocialCompliance = lazy(() => import("./pages/SocialCompliance"));

function App() {
  const user = useRecoilValue(userAtom);
  const isAuthenticated = user && Object.keys(user).length !== 0; // Update this based on how you're tracking authenticated users
  const isDevModeEnabled =
    process.env.REACT_APP_DEV_MODE === "true" ||
    process.env.REACT_APP_DEV_MODE === true;

  return (
    <Router>
      <GoogleOAuthProvider clientId="701177215582-m10tn0hun38pudm6aqdrp4asei35pm1q.apps.googleusercontent.com">
        <div className="App">
          <Suspense fallback={null}>
          <Routes>
            <Route path="/" element={<EarlyAccess />} />
            <Route path="/privacy" element={<Privacy />} />
            <Route path="/login" element={<Login />} />
            {process.env.NODE_ENV === "development" && (
              <Route path="/test" element={<ExcelViewer />} />
            )}
            <Route
              path="/work/:timelineID"
              element={<ApprovalRequestTimeL />}
            />
            <Route path="/signup" element={<Navigate to="/login" replace />} />
            <Route path="/accept-invite" element={<InviteHandler />} />
            <Route path="/select-plan" element={<PlanSelect />} />
            <Route path="/forgot-password" element={<ForgotPassword />} />
            <Route path="/2fa-login" element={<Verify2FaAndLogin />} />
            <Route path="/reset-2fa" element={<Reset2FA />} />
            <Route
              path="/:platform/oauth/callback"
              element={<OauthCallback />}
            />
            <Route
              path="/workspaces"
              element={
                isAuthenticated ? <Workspaces /> : <Navigate to="/login" />
              }
            />
            <Route
              path="/settings"
              element={
                isAuthenticated ? <NewSettings /> : <Navigate to="/login" />
              }
            />
            <Route
              path="/approver-notification-preferences"
              element={
                isAuthenticated ? (
                  <NewSettings activePath="approver-notification-preferences" />
                ) : (
                  <Navigate to="/login" />
                )
              }
            />
            <Route
              path="/org-settings"
              element={
                isAuthenticated ? (
                  <NewSettings activePath="org-settings" />
                ) : (
                  <Navigate to="/login" />
                )
              }
            />
            <Route
              path="/ai-assistant"
              element={
                isAuthenticated ? (
                  <NewSettings activePath="ai-assistant" />
                ) : (
                  <Navigate to="/login" />
                )
              }
            />
            <Route
              path="/audit-trail"
              element={
                isAuthenticated ? (
                  <NewSettings activePath="audit-trail" />
                ) : (
                  <Navigate to="/login" />
                )
              }
            />
            <Route
              path="/contacts"
              element={
                isAuthenticated ? (
                  <NewSettings activePath="contacts" />
                ) : (
                  <Navigate to="/login" />
                )
              }
            />
            <Route
              path="/social-profile-compliance"
              element={
                isAuthenticated ? (
                  <NewSettings activePath="social-profile-compliance" />
                ) : (
                  <Navigate to="/login" />
                )
              }
            />
            <Route
              path="/billing"
              element={
                isAuthenticated ? (
                  <NewSettings activePath="billing" />
                ) : (
                  <Navigate to="/login" />
                )
              }
            />
            <Route
              path="/manage-users"
              element={
                isAuthenticated ? (
                  <NewSettings activePath="manage-users" />
                ) : (
                  <Navigate to="/login" />
                )
              }
            />
            <Route
              path="/contact-us"
              element={
                isAuthenticated ? (
                  <NewSettings activePath="contact-us" />
                ) : (
                  <Navigate to="/login" />
                )
              }
            />
            <Route
              path="/integrations"
              element={
                isAuthenticated ? (
                  <NewSettings activePath="integrations" />
                ) : (
                  <Navigate to="/login" />
                )
              }
            />
            <Route
              path="/onboard"
              element={isAuthenticated ? <Onboard /> : <Navigate to="/login" />}
            />
            <Route
              path="/use"
              element={
                isAuthenticated ? <Settings /> : <Navigate to="/login" />
              }
            />
            <Route
              path="/profile"
              element={isAuthenticated ? <Profile /> : <Navigate to="/login" />}
            />
            <Route
              path="/security"
              element={
                isAuthenticated ? (
                  <Profile activePath="security" />
                ) : (
                  <Navigate to="/login" />
                )
              }
            />
            <Route
              path="/notifications"
              element={
                isAuthenticated ? (
                  <NotificationList />
                ) : (
                  <Navigate to="/login" />
                )
              }
            />
            <Route
              path="/requests"
              element={
                isAuthenticated ? <Requests /> : <Navigate to="/login" />
              }
            />
            <Route
              path="/create-request"
              element={
                isAuthenticated ? <CreateApprovalR /> : <Navigate to="/login" />
              }
            />
            <Route
              path="/drop/create-request"
              element={
                isAuthenticated ? (
                  <CreateApprovalDrop />
                ) : (
                  <Navigate to="/login" />
                )
              }
            />
            <Route
              path="/drops"
              element={isAuthenticated ? <MyDrops /> : <Navigate to="/login" />}
            />
            <Route
              path="/swift-check"
              element={
                isAuthenticated ? <SwiftCheck /> : <Navigate to="/login" />
              }
            />
            <Route
              path="/social-compliance"
              element={
                isAuthenticated ? <SocialCompliance /> : <Navigate to="/login" />
              }
            />
            <Route
              path="/drops/:dropToken"
              element={<CompleteApprovalDrop />}
            />
            <Route
              path="/create-request-email"
              element={
                isAuthenticated ? (
                  <CreateApprovalRequestEmail />
                ) : (
                  <Navigate to="/login" />
                )
              }
            />
            <Route
              path="/workspace/:workspaceID"
              element={
                isAuthenticated ? <Approvals /> : <Navigate to="/login" />
              }
            />
            <Route path="/review" element={<ReviewApprovalR />} />
            <Route path="/requests-dashboard/:userId" element={<RequestsR />} />
            <Route
              path="/edits/:approvalReqID"
              element={
                isAuthenticated ? (
                  <ReviewApprovalRequest />
                ) : (
                  <Navigate to="/login" />
                )
              }
            />
            <Route
              path="/edit/:approvalReqID"
              element={
                isAuthenticated ? <EditApprovalR /> : <Navigate to="/login" />
              }
            />
            <Route
              path="/edit-email/:approvalReqID"
              element={
                isAuthenticated ? (
                  <EditApprovalRequestEmail />
                ) : (
                  <Navigate to="/login" />
                )
              }
            />
            <Route
              path="/approvalRequest/:timelineID"
              element={
                isAuthenticated ? (
                  <ApprovalRequestTimeL />
                ) : (
                  <Navigate to="/login" />
                )
              }
            />
            <Route
              path="/verify-email/:firebaseAuthUUID"
              element={<VerifyEmail />}
            />
            <Route
              path="/admin/backoffice/ai-prompts"
              element={
                <RequireBackofficeAdmin>
                  <AdminBackofficeAiPrompt />
                </RequireBackofficeAdmin>
              }
            />
            <Route
              path="/admin/backoffice/live-monitor"
              element={
                <RequireBackofficeAdmin>
                  <AdminBackofficeLiveMonitor />
                </RequireBackofficeAdmin>
              }
            />
            <Route
              path="/admin/backoffice/prompt-templates"
              element={
                <RequireBackofficeAdmin>
                  <AdminBackofficePromptTemplates />
                </RequireBackofficeAdmin>
              }
            />
            <Route
              path="/admin/backoffice/instant-monitor"
              element={
                <RequireBackofficeAdmin>
                  <AdminInstantMonitor />
                </RequireBackofficeAdmin>
              }
            />
            <Route
              path="/admin/backoffice/feature-flags"
              element={
                <RequireBackofficeAdmin>
                  <AdminBackofficeFeatureFlags />
                </RequireBackofficeAdmin>
              }
            />
            {isDevModeEnabled && (
              <Route path="/test-page" element={<SignaturePad />} />
            )}
            <Route path="*" element={<PNF />} />
          </Routes>
          </Suspense>
        </div>
      </GoogleOAuthProvider>
    </Router>
  );
}

export default App;
